import invariant from "tiny-invariant"
import {
  Big,
  type BigintIsh,
  Fraction,
  MAX_UINT256,
  Rounding,
  ZERO,
} from "../math"

import type { Hex } from "viem"
import { Native } from "./Native.js"
import { Share } from "./Share.js"
import { Token } from "./Token.js"
import { type SerializedAmount, amountSchema } from "./schema"
import type { TokenType } from "./types"

/**
 * The Amount class represents a quantity of a specific currency.
 * It provides methods for mathematical operations, conversions,
 * and utility functions to work with amounts of this currency.
 * This class extends the Fraction base class, providing additional
 * methods and properties specific to a currency amount, such as handling
 * token or ether raw amounts, fractional representations, and serialization.
 *
 * Each instance of the Amount class is associated with a specific
 * TokenType, ensuring type safety and proper calculations across
 * different token types.
 *
 * Examples of usage include representing balances, prices,
 * or any other numeric values denominated in the given currency.
 */
export class Amount<T extends TokenType> extends Fraction {
  /**
   * The currency associated with this Amount.
   * @type {TokenType}
   */
  readonly currency: T
  /**
   * Scaling factor based on the currency's decimal places.
   * @type {bigint}
   */
  readonly scale: bigint
  /**
   * Returns a new currency amount instance from the unitless amount of token, i.e. the raw amount
   * @param currency the currency in the amount
   * @param rawAmount the raw token or ether amount
   * @returns {Amount<TokenType>}
   */
  static fromRawAmount<T extends TokenType>(
    currency: T,
    rawAmount: BigintIsh,
  ): Amount<T> {
    return new Amount(currency, rawAmount)
  }

  /**
   * Constructs an Amount instance from a share value considering optional rebase calculation.
   * @param currency The currency type of the amount.
   * @param shares The shares amount.
   * @param rebase Object with base and elastic values for rebase calculations.
   * @param roundUp If true, rounds up the result.
   * @returns {Amount<TokenType>}
   */
  static fromShare<T extends TokenType>(
    currency: T,
    shares: BigintIsh,
    rebase: { base: bigint; elastic: bigint },
    roundUp = false,
  ): Amount<T> {
    if (rebase.base === ZERO) return new Amount(currency, shares)

    const sharesBI =
      typeof shares === "bigint" ? shares : BigInt(shares.toString())

    const elastic = (sharesBI * rebase.elastic) / rebase.base

    if (roundUp && (elastic * rebase.base) / rebase.elastic < sharesBI) {
      return new Amount(currency, elastic + 1n)
    }

    return new Amount(currency, elastic)
  }

  /**
   * Internal constructor for the Amount class.
   * @param currency The currency type of the amount.
   * @param numerator The numerator for fractional value.
   * @param denominator The denominator for fractional value (optional).
   */
  protected constructor(
    currency: T,
    numerator: BigintIsh,
    denominator?: BigintIsh,
  ) {
    super(numerator, denominator)
    invariant(this.quotient <= MAX_UINT256, "AMOUNT")
    this.currency = currency
    this.scale = 10n ** BigInt(currency.decimals)
  }

  /**
   * Converts the current Amount to a Share representation.
   * @param rebase Contains base and elastic values for conversion.
   * @param roundUp If true, rounds up the final result.
   * @returns {Share<TokenType>}
   */
  toShare(rebase: { base: bigint; elastic: bigint }, roundUp = false) {
    if (rebase.elastic === ZERO)
      return Share.fromRawShare(this.currency, this.quotient)

    const base = (this.quotient * rebase.base) / rebase.elastic

    if (roundUp && (base * rebase.elastic) / rebase.base < this.quotient) {
      return Share.fromRawShare(this.currency, base + 1n)
    }

    return Share.fromRawShare(this.currency, base)
  }

  /**
   * Construct a currency amount with a denominator that is not equal to 1
   * @param currency the currency
   * @param numerator the numerator of the fractional token amount
   * @param denominator the denominator of the fractional token amount
   * @returns {Amount<TokenType>}
   */
  static fromFractionalAmount<T extends TokenType>(
    currency: T,
    numerator: BigintIsh,
    denominator: BigintIsh,
  ): Amount<T> {
    return new Amount(currency, numerator, denominator)
  }

  /**
   * Adds two Amounts of the same currency.
   * @param other The other Amount to add.
   * @returns {Amount<TokenType>}
   */
  override add(other: Amount<T>): Amount<T> {
    invariant(this.currency.equals(other.currency), "CURRENCY")
    const added = super.add(other)
    return Amount.fromFractionalAmount(
      this.currency,
      added.numerator,
      added.denominator,
    )
  }

  /**
   * Subtracts another Amount from the current Amount.
   * @param other The Amount to subtract.
   * @returns {Amount<TokenType>}
   */
  override subtract(other: Amount<T>): Amount<T> {
    invariant(this.currency.equals(other.currency), "CURRENCY")
    const subtracted = super.subtract(other)
    return Amount.fromFractionalAmount(
      this.currency,
      subtracted.numerator,
      subtracted.denominator,
    )
  }

  /**
   * Multiplies the current Amount by another fraction or number.
   * @param other Fraction or number to multiply by.
   * @returns {Amount<TokenType>}
   */
  override multiply(other: Fraction | BigintIsh): Amount<T> {
    const multiplied = super.multiply(other)
    return Amount.fromFractionalAmount(
      this.currency,
      multiplied.numerator,
      multiplied.denominator,
    )
  }

  /**
   * Divides the current Amount by another fraction or number.
   * @param other Fraction or number to divide by.
   * @returns {Amount<TokenType>}
   */
  override divide(other: Fraction | BigintIsh): Amount<T> {
    const divided = super.divide(other)
    return Amount.fromFractionalAmount(
      this.currency,
      divided.numerator,
      divided.denominator,
    )
  }

  /**
   * Converts the Amount to a significant representation string.
   * @param significantDigits Number of significant digits.
   * @param format Formatting options for the output.
   * @param rounding Rounding method for the conversion.
   * @returns {string}
   */
  override toSignificant(
    significantDigits = 6,
    format?: object,
    rounding: Rounding = Rounding.ROUND_DOWN,
  ): string {
    return super
      .divide(this.scale)
      .toSignificant(significantDigits, format, rounding)
  }

  /**
   * Returns a fixed decimal string representation of the Amount.
   * @param decimalPlaces Number of decimal places in the output.
   * @param format Formatting options for the output.
   * @param rounding Rounding method for the conversion.
   * @returns {string}
   */
  override toFixed(
    decimalPlaces: number = this.currency.decimals,
    format?: object,
    rounding: Rounding = Rounding.ROUND_DOWN,
  ): string {
    const resolvedDecimalPlaces =
      decimalPlaces > this.currency.decimals
        ? this.currency.decimals
        : decimalPlaces
    return super
      .divide(this.scale)
      .toFixed(resolvedDecimalPlaces, format, rounding)
  }

  /**
   * Converts the `Fraction` into a string representation with number format compact notation over 1 million.
   * @param {number} decimalPlaces - The number of decimal places to display.
   * @param format Formatting options for the output.
   * @param {Rounding} rounding - The rounding mode to use.
   * @returns {string} - A string representation of the `Fraction`.
   * @throws {Error} - Throws an error if the decimalPlaces parameter is not valid.
   */
  override toNotation(
    decimalPlaces: number = this.currency.decimals,
    format?: object,
    rounding: Rounding = Rounding.ROUND_DOWN,
  ) {
    const resolvedDecimalPlaces =
      decimalPlaces > this.currency.decimals
        ? this.currency.decimals
        : decimalPlaces
    return super
      .divide(this.scale)
      .toNotation(resolvedDecimalPlaces, format, rounding)
  }

  /**
   * Converts the Amount to a hexadecimal string.
   * @returns {Hex}
   */
  toHex(): Hex {
    return `0x${this.quotient.toString(16)}`
  }

  /**
   * Returns an exact string representation of the Amount.
   * @param format Formatting options for the output.
   * @returns {string}
   */
  toExact(format: object = { groupSeparator: "" }): string {
    Big.DP = this.currency.decimals
    return new Big(this.quotient.toString())
      .div(this.scale.toString())
      .toFormat(format)
  }

  /**
   * Provides a wrapped version of the Amount.
   * @returns {Amount<Token>}
   */
  get wrapped(): Amount<Token> {
    if (this.currency.isToken) return this as Amount<Token>
    return Amount.fromFractionalAmount(
      this.currency.wrapped,
      this.numerator,
      this.denominator,
    )
  }

  /**
   * Serializes the Amount into a specific format.
   * @returns {SerializedAmount}
   */
  serialize(): SerializedAmount {
    return amountSchema.parse({
      amount: this.quotient.toString(),
      currency: this.currency.serialize(),
    })
  }

  /**
   * Converts serialized data back into an Amount instance.
   * @param amount The serialized Amount data.
   * @returns {Amount<TokenType>}
   */
  static deserialize<T extends TokenType>(amount: SerializedAmount): Amount<T> {
    if (amount.currency.isNative)
      return Amount.fromRawAmount(
        Native.deserialize(amount.currency) as T,
        amount.amount,
      )
    return Amount.fromRawAmount(
      Token.deserialize(amount.currency) as T,
      amount.amount,
    )
  }
}
